/**
 * Momentum cluster — first proof batch of cookbook seed recipes.
 *
 * Each entry validates against the real buildGraphFromRecipe engine.
 * Run the companion test to confirm every recipe builds with zero errors.
 *
 * Node-type and handle references were verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 */

import type { CookbookEntry } from './types'

// ── 1. Opening-Range Breakout ─────────────────────────────────
//
// Pattern: compute the high of the opening range (9:30–9:45) via a TimeSlice
// node in 'max' mode over the high field, then fire entry when close crosses
// above that level.
//
// Nodes: priceData → timeSlice (opening max-high) → condition (close > orh) ┐
//        timeSignal (true after 09:45) ───────────────────────────────────── ┴ merge(AND) → Trade Manager
// The timeSignal is the merge's second input, so a trade can't fire while the
// opening range is still forming (before 09:45).
const openingRangeBreak: CookbookEntry = {
  key: 'opening-range-break',
  name: 'Opening Range Breakout',
  category: 'momentum',
  summary: 'Long entry when price closes above the 9:30–9:45 opening range high.',
  difficulty: 'beginner',
  tags: ['Breakout', 'Opening range'],
  whenToUse:
    'Works best in strong trending markets when a stock has already shown pre-market or early momentum. Most reliable on higher-relative-strength names where the opening session is extending a broader trend rather than reversing one.',
  whatToLookFor:
    'A clean break and close above the range high confirms the setup; a close back inside the opening range or a failure to hold the breakout level invalidates it. Volume expansion on the break strengthens the signal.',
  howItWorks:
    "A timeSlice node isolates the 9:30–9:45 window and collapses it to a single value — the highest high of that opening period. A condition node fires when the current close pushes above that level. A timeSignal node turns true only after 9:45, and a merge node AND-combines it with the breakout — so a trade can't fire while the range is still forming. The Trade Manager then opens a long (its default 2% target and 1% stop) the moment that gate is satisfied. You're buying the first sustained push above the range the market set at the open, with the range high as a natural, pre-defined risk reference.",
  nodeNotes: {
    price:
      'The data source — OHLCV bars for your symbol on a 5-minute timeframe. Every node downstream reads from here.',
    orh: 'Isolates the 9:30–9:45 opening window and reduces it to the single highest high — the opening range high you trade against.',
    breakout:
      'Fires the instant the current close pushes above that opening range high — the definitive break of the session’s opening balance.',
    after_range:
      'Turns true only after 9:45, once the opening range is fully formed. It gates out premature signals during the range-building window.',
    gate: 'Requires BOTH the breakout AND the after-range gate to be true, so a trade can only trigger once price breaks out and the opening range has finished forming.',
  },
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Opening range high: max of the 'high' field from 09:30 to 09:45
      id: 'orh',
      type: 'timeSlice',
      params: {
        mode: 'max',
        startTime: '09:30',
        endTime: '09:45',
        startOffset: 0,
        endOffset: 0,
      },
      inputs: { input_1: 'price.high' },
    },
    {
      // Fire when current close exceeds the opening range high
      id: 'breakout',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'orh.value' },
    },
    {
      // True only after the opening range completes (09:45+), so entries can't
      // fire mid-range while the high is still being established.
      id: 'after_range',
      type: 'timeSignal',
      params: { startTime: '09:45', endTime: '16:00' },
    },
    {
      // Gate: require the breakout AND the after-range signal together.
      id: 'gate',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'breakout.true', input_2: 'after_range.true' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'gate.result' },
  },
}

// ── 2. MA Crossover (Golden Cross) ───────────────────────────
//
// Pattern: fast SMA crosses above slow SMA on the daily timeframe.
// crossType 'crosses-above' ensures only the upward cross triggers.
//
// Nodes: priceData → fast SMA(50) + slow SMA(200) → crossover(crosses-above) → Trade Manager
const maCrossover: CookbookEntry = {
  key: 'ma-crossover',
  name: 'MA Crossover (Golden Cross)',
  category: 'momentum',
  summary: 'Daily long entry when the 50-period SMA crosses above the 200-period SMA.',
  difficulty: 'beginner',
  tags: ['Trend', 'Moving average'],
  whenToUse:
    'Best applied in trending markets after a prolonged bearish phase has fully resolved. Because the 50/200 cross is a lagging confirmation signal, it is more useful as a regime filter — establishing that the long-term trend has turned bullish — than as a precise entry trigger.',
  whatToLookFor:
    'The cross itself is the signal; a clean separation of the two averages after the cross, with price staying above both, adds confidence. Rapid back-and-forth crosses in a choppy or sideways market indicate a range environment where this setup whipsaws repeatedly.',
  howItWorks:
    'Two simplemovingaverage nodes compute a fast (50-bar) and a slow (200-bar) SMA from the same daily close stream. A crossover node watches both outputs and emits a signal only when the fast line crosses above the slow line — the classic golden cross. The Trade Manager turns that single crossover event into a long order. Because moving averages are built from past prices, the signal confirms a trend rather than predicting one, so it is most useful for framing directional bias rather than precise market-timing.',
  nodeNotes: {
    price: 'The data source. Daily bars, so every period below is counted in days.',
    fast_sma:
      'The faster line: a 50-period average of the close. It reacts to recent moves sooner, so it is the one that does the crossing.',
    slow_sma:
      'The slower line: a 200-period average of the same close. It barely moves week to week, which is what makes it a usable definition of the longer trend.',
    cross:
      'The whole strategy, in one node. Cross Type is set to Crosses Above, so this is true on the single bar where the 50 pushes up through the 200, and ignored when it crosses back down. A Condition here instead would be true every day of the uptrend rather than once at its start.',
  },
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '1D' },
    },
    {
      id: 'fast_sma',
      type: 'simplemovingaverage',
      params: { period: 50 },
      inputs: { input_1: 'price.close' },
    },
    {
      id: 'slow_sma',
      type: 'simplemovingaverage',
      params: { period: 200 },
      inputs: { input_1: 'price.close' },
    },
    {
      id: 'cross',
      type: 'crossover',
      params: { crossType: 'crosses-above' },
      inputs: { input_1: 'fast_sma.sma', input_2: 'slow_sma.sma' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'cross.signal' },
  },
}

// ── 3. VWAP Rejection ────────────────────────────────────────
//
// Pattern: price failing to reclaim VWAP — close stays below VWAP for several
// consecutive bars (held via timeout / Signal Hold). Good for short-side setups.
//
// Nodes: priceData → vwap → condition(close < vwap) → timeout(hold N bars) → Trade Manager
//
// Note: entryRules.signal only declares acceptedTypes ['number','ohlcv'] in the
// schema but the builder does not type-check connections — that is the validator's
// job. The build step succeeds; live validator warnings are expected on this shape.
const vwapRejection: CookbookEntry = {
  key: 'vwap-rejection',
  name: 'VWAP Rejection (Below VWAP Hold)',
  category: 'momentum',
  summary: 'Short entry when price persists below VWAP for multiple consecutive bars.',
  difficulty: 'intermediate',
  tags: ['Rejection', 'VWAP'],
  whenToUse:
    'Most relevant intraday on parabolic or extended stocks that have failed to reclaim VWAP after a bounce attempt. Works best in a weak market environment where sellers are clearly in control and institutions are not buying dips.',
  whatToLookFor:
    'Sustained closes below VWAP with failed retests — each attempted reclaim rolls over — confirm the rejection. A clean reclaim and hold of VWAP above would invalidate the short premise and signal the setup has failed.',
  howItWorks:
    'A vwap node takes the full OHLCV stream and computes the session\'s volume-weighted average price. A condition node checks whether each 5-minute close is below that VWAP level. A timeout node holds the "below VWAP" boolean active for up to 5 bars, preventing a single bar\'s close from prematurely resetting the signal — this models the persistence requirement of a true VWAP rejection rather than a momentary dip. The Trade Manager, set to Short, turns the held signal into a short order. The timeout is what distinguishes a sustained rejection from a brief wick below VWAP.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // VWAP requires full OHLCV — use the ohlcv output handle directly
      id: 'vwap',
      type: 'vwap',
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      id: 'below_vwap',
      type: 'condition',
      params: { operator: '<' },
      inputs: { input_1: 'price.close', input_2: 'vwap.vwap' },
    },
    {
      // Hold the "below VWAP" signal for up to 5 bars before it resets
      id: 'hold',
      type: 'timeout',
      params: { period: 5 },
      inputs: { signal: 'below_vwap.true' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Short' },
    feeds: { socket: 'trigger', from: 'hold.output' },
  },
}

// ── 5. ATR-Based Initial Stop ────────────────────────────────
//
// Pattern: place the initial stop one ATR below the close. The Trade
// Manager's Price level stop reads a price, frozen at entry, so the recipe
// computes close - ATR. (It used to wire the raw ATR, about $1, in as the
// stop price.) Pair with any entry recipe in a real strategy.
//
// Nodes: priceData → sma(1) + averagetruerange(14) → subtract → Trade Manager stop
const atrStop: CookbookEntry = {
  key: 'atr-stop',
  name: 'ATR-Based Initial Stop',
  category: 'momentum',
  summary: "Initial stop placement calibrated to the stock's actual 14-bar volatility via ATR.",
  difficulty: 'beginner',
  tags: ['ATR'],
  whenToUse:
    "Applicable to any entry recipe — swap in place of a fixed-percentage stop whenever the instrument's volatility varies meaningfully across symbols or market regimes. Particularly useful on stocks with wide intraday ranges where a fixed-% stop would either be too tight (stop hunted on normal noise) or too wide (risk exceeds the trade's merit).",
  whatToLookFor:
    "The stop distance automatically widens during high-volatility periods (earnings, news) and tightens during quiet consolidation, which is the correct behavior. If the ATR-derived stop is still wider than the stock's average daily range, the entry itself is likely too risky to take.",
  howItWorks:
    "An averagetruerange node takes the full OHLCV stream and computes a 14-bar ATR — a volatility-calibrated ruler that reflects how much the asset has been moving. subtract takes that ATR off the close to give a stop price, and the Trade Manager's stop, set to Price level, fixes that price at entry instead of applying a fixed percentage. This means the stop distance is dynamically sized to the stock's actual recent movement rather than a uniform percentage that ignores volatility differences across instruments and market regimes.",
  nodeNotes: {
    price: 'The data source, on 5-minute bars.',
    atr: 'A 14-period Average True Range. It reads the full OHLCV wire rather than a single field, because a true range needs the high and the low of each bar, not just the close.',
    close_num: 'The close as a plain number, so it can be subtracted from.',
    stop_price:
      'The close minus one ATR: the stop price for a long. The Trade Manager stop, set to Price level, fixes it at entry. That is the entire point: on a volatile day the stop sits further away and on a quiet day it tightens, without you changing anything.',
  },
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // ATR needs the full OHLCV stream — no field selection on this node
      id: 'atr',
      type: 'averagetruerange',
      params: { period: 14 },
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      // Period-1 SMA is a numeric pass-through for close price (ohlcv → number)
      id: 'close_num',
      type: 'simplemovingaverage',
      params: { period: 1 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Stop price = close - ATR (a long's stop)
      id: 'stop_price',
      type: 'subtract',
      inputs: { input_1: 'close_num.sma', input_2: 'atr.atr' },
    },
  ],
  tradeManager: {
    params: { stopType: 'Static Value' },
    feeds: { socket: 'stopLevel', from: 'stop_price.value' },
  },
}

// ── 6. MA Trailing Stop ──────────────────────────────────────
//
// Pattern: trail the exit by a moving average — exit when close drops below
// the MA.  trailingStop.signal accepts boolean|number; we wire the SMA value
// directly (number). trailType 'Condition' is the semantic match here, but
// the node builds cleanly with any trailType since it's just a param.
//
// Nodes: priceData → simplemovingaverage(20) → trailingStop
const maTrailingStop: CookbookEntry = {
  key: 'ma-trailing-stop',
  name: 'MA Trailing Stop',
  category: 'momentum',
  summary: 'Trails an open winner by a 20-bar moving average — exits on the first close below it.',
  difficulty: 'beginner',
  whenToUse:
    'Pair with any entry recipe on a trending stock to manage the back half of a winner. Best suited for momentum trades where the goal is to hold through normal pullbacks but exit cleanly when the trend genuinely breaks, rather than taking a fixed profit target.',
  whatToLookFor:
    'As long as the stock closes above the 20-bar SMA on each bar, the trail keeps the position alive. The first close below the MA is the exit — intraday wicks below the line do not count. A tighter 10-bar MA can be substituted for faster-moving stocks at the cost of more frequent premature exits.',
  howItWorks:
    'A simplemovingaverage node computes a 20-bar SMA from the 5-minute close stream, producing a smooth, continuously updated price level that rises as the trend advances. A trailingStop node receives that SMA value directly on its signal input, with trailType set to "Condition." This wires the moving average itself as the dynamic exit level, so the stop rises with the trend as long as the position is profitable and fires the exit the moment price closes below the average — turning the MA from a visual reference into an automated position manager.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      id: 'ma',
      type: 'simplemovingaverage',
      params: { period: 20 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Trail exit driven by the MA value fed into trailingStop's signal input
      id: 'trail',
      type: 'trailingStop',
      params: { trailType: 'Condition' },
      inputs: { signal: 'ma.sma' },
    },
  ],
  unavailable:
    'The Trade Manager trails by a percent or ticks; it cannot trail a moving average yet.',
}

// ── Export ───────────────────────────────────────────────────

export const momentumSeedRecipes: CookbookEntry[] = [
  openingRangeBreak,
  maCrossover,
  vwapRejection,
  atrStop,
  maTrailingStop,
]
